import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { scroller } from 'react-scroll';

import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import cn from 'classnames';
import { colors } from '../../constants/styleConstants';
import { useTranslation } from '../../src/i18n';
import { usePageScroll } from '../../utils/hooks/usePageScroll';

const useStyles = makeStyles(theme => ({
  appBar: {
    backgroundColor: colors.white,
    top: 85,
    boxShadow: 'none',
    borderBottom: '1px solid lightgray',
    maxWidth: '100%',
    zIndex: 900,
    color: colors.primary,
    [theme.breakpoints.down('sm')]: {
      top: 110,
    },
  },
  selected: {
    '& .MuiTab-wrapper': {
      fontWeight: 'bold',
    },
  },
  tabWrapper: {
    '& a': {
      color: 'inherit',
      '&:hover': {
        color: `${colors.primary} !important`,
      },
    },
  },
  tabRoot: {
    maxWidth: 'fit-content',
  },
}));

export const FbTabs = ({
  tabs,
  scrollable,
  onChange,
  initialTabValue,
  value,
  orientation,
  appBarClass,
  tabsClasses,
  tabClasses,
  preventPageScroll,
}) => {
  const classes = useStyles();
  const isScrollable = scrollable !== false;
  const { t } = useTranslation();

  const [internalValue, setValue] = React.useState(initialTabValue);
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('sm'));

  const { setPreventPageScroll } = usePageScroll();

  const handleChange = (event, newValue) => {
    if (preventPageScroll) {
      setPreventPageScroll(true);
    }
    setValue(newValue);
    scroller.scrollTo(newValue, { offset: mobileView ? -180 : -150, smooth: true, duration: 250 });
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <AppBar position={isScrollable ? `sticky` : 'static'} className={cn(classes.appBar, appBarClass)}>
      <Tabs
        value={value || internalValue}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        indicatorColor="primary"
        textColor="primary"
        orientation={orientation}
        classes={tabsClasses}
      >
        {tabs.map(item => {
          const name = t(get(item, 'name', ''));
          return isScrollable ? (
            <Tab
              classes={{
                selected: classes.selected,
                wrapper: classes.tabWrapper,
                root: classes.tabRoot,
                ...tabClasses,
                ...(item?.classes || {}),
              }}
              key={`tab-${name}`}
              value={get(item, 'value', '')}
              id={item?.value}
              label={<span>{name}</span>}
            ></Tab>
          ) : (
            <Tab
              classes={{ selected: classes.selected, root: classes.tabRoot, ...tabClasses, ...(item?.classes || {}) }}
              label={name}
              key={`tab-${name}`}
              value={get(item, 'value', '')}
            />
          );
        })}
      </Tabs>
    </AppBar>
  );
};

FbTabs.propTypes = {
  initialTabValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      href: PropTypes.string,
    }),
  ).isRequired,
  scrollable: PropTypes.bool,
  onChange: PropTypes.func,
  orientation: PropTypes.string,
  tabsClasses: PropTypes.object,
  tabClasses: PropTypes.object,
  preventPageScroll: PropTypes.bool,
};

FbTabs.defaultProps = {
  scrollable: false,
  orientation: 'horizontal',
  tabsClasses: {},
  tabClasses: {},
  preventPageScroll: true,
};

export default FbTabs;
